import React, { useState } from 'react';

import { isURL } from 'class-validator';

import ConfirmModal from '@/src/components/common/Modal/ConfirmModal/ConfirmModal';

export interface EditReportModalProps {
  showModal: boolean;
  toggleShowModal: (show: boolean) => void;
  defaultName?: string;
  defaultUrl?: string;
  onSave: (name: string, url: string) => void;
  onDelete: () => void;
}

export const EditReportModal: React.FC<EditReportModalProps> = ({
  showModal,
  toggleShowModal,
  defaultName = '',
  defaultUrl = '',
  onSave,
  onDelete,
}) => {
  const [name, setName] = useState(defaultName);
  const [url, setUrl] = useState(defaultUrl);
  const [nameTouched, setNameTouched] = useState(false);

  const isNameValid = !!name.trim();
  const isValidUrl = isURL(url);
  // Enable save if name is not empty and URL is empty or valid
  const isValidOrEmpty = isNameValid && (!url || !url.trim() || isValidUrl);

  const handleSave = () => {
    if (!isValidOrEmpty) return;
    onSave(name, url);
    toggleShowModal(false);
  };

  const handleDelete = () => {
    onDelete();
    toggleShowModal(false);
  };

  const handleCancel = () => toggleShowModal(false);

  const content = (
    <>
      <div className="tw-mb-4">
        <div className="tw-block tw-mb-1 tw-font-brandonTextMedium">Report Name</div>
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!nameTouched) setNameTouched(true);
          }}
          onBlur={() => setNameTouched(true)}
          className={
            'tw-w-full tw-border tw-rounded tw-px-2 tw-py-1' +
            (!isNameValid && nameTouched ? ' error' : '')
          }
          placeholder="Enter report name"
        />
        {!isNameValid && nameTouched ? (
          <div
            style={{
              marginTop: 8,
              background: '#d32f2f',
              color: 'white',
              borderRadius: '6px',
              padding: '6px 16px',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
          >
            Report name required
          </div>
        ) : null}
      </div>
      <div className="tw-mb-4" style={{ position: 'relative' }}>
        <div className="tw-block tw-mb-1 tw-font-brandonTextMedium">Report URL</div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className={
              'tw-w-full tw-border tw-rounded tw-px-2 tw-py-1' +
              (url && !isValidOrEmpty ? ' error' : '')
            }
            placeholder="Enter report URL"
            style={{ marginRight: url && !isValidUrl ? 8 : 0 }}
          />
          {url && !isValidUrl ? (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#d32f2f',
                color: 'white',
                borderRadius: '6px',
                padding: '6px 16px',
                fontSize: '14px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                whiteSpace: 'nowrap',
                zIndex: 10,
                display: 'inline-block',
                marginLeft: 8,
              }}
            >
              Invalid URL
            </div>
          ) : null}
        </div>
      </div>
      <div className="tw-flex tw-justify-between tw-items-center tw-gap-2 tw-mt-12 tw-mb-6">
        <button
          type="button"
          className="tw-bg-red-600 tw-text-white tw-px-4 tw-py-2 tw-rounded hover:tw-bg-red-700"
          onClick={handleDelete}
        >
          Delete
        </button>
        <div className="tw-flex tw-gap-2">
          <button
            type="button"
            className="tw-bg-gray-200 tw-text-gray-700 tw-px-4 tw-py-2 tw-rounded hover:tw-bg-gray-300"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tw-bg-cfo-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded hover:tw-bg-cfo-blue-700"
            onClick={handleSave}
            disabled={!isValidOrEmpty}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );

  return (
    <ConfirmModal
      id="edit-report-modal"
      open={showModal}
      title="Edit Embedded Report"
      message={content}
      modalWidth="548px"
      cancelBtn={false}
      confirmBtn={false}
      onClose={handleCancel}
      onCancel={handleCancel}
      onConfirm={undefined}
    />
  );
};
